import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import {
  RecordContextProvider,
  Form,
  TabbedShowLayout,
  useCreate,
  useGetOne,
  useNotify,
  usePermissions,
  useStore,
  useUpdate,
  useDelete,
} from 'react-admin';
import { ChevronRight } from '@mui/icons-material';
import { isEqual } from 'lodash';

import TransactionSummaryTable from '../transaction/TransactionSummaryTable';
import entityTypes, { KnownEntityTypes } from '../../constants/entityTypes';
import TransactionTableSetupType from '../../constants/transactionTableSetup';
import { TransactionTypeConfiguration } from '../transaction/AddTransactionTypeDialogButton';
import actions, { Actions } from '../../constants/actions';
import hasAccess from '../../utilities/hasAccess';
import RequestGraphsStatsResponse from '../../constants/RequestGraphsStatsResponse';
import { TransactionTableColumnSource } from '../../constants/transactionTableColumnSources';
import entityListPermissionMap from '../../constants/entityPermissionMap';

export type DemoData = {
  data: RequestGraphsStatsResponse;
  isLoading: boolean;
};

const dummyData = {
  data: {
    id: '1234567890',
    outgoing: {
      frequency: 20,
      volume: 12000,
    },
    incoming: {
      frequency: 10,
      volume: 12000,
    },
    processing: {
      frequency: 10,
      volume: 12000,
    },
    total: {
      frequency: 10,
      volume: 12000,
    },
  },
};

const EntityTransactionTableSetup = ({ entityType }: {entityType: KnownEntityTypes}) => {
  const preferenceKey = `transaction-summary-table-${entityType}-setup`;

  const requiredPermission = entityListPermissionMap[entityType];
  const { permissions } = usePermissions<Actions[]>();
  const hasRequiredPermissions = hasAccess(permissions, requiredPermission);
  const {
    data: defaultConfiguration,
    isLoading: isLoadingDefaultConfig,
  } = useGetOne<TransactionTableSetupType>(
    'transaction-table-setup',
    { id: `${entityType}` },
    {
      enabled: hasRequiredPermissions,
    },
  );

  const [currentColumns] = useStore<string[] | undefined>(`preferences.${preferenceKey}.columns`);
  const [availableColumns] = useStore<{
    index: string;
    source: TransactionTableColumnSource;
    label: string;
  }[]>(`preferences.${preferenceKey}.availableColumns`);

  const [updateOne] = useUpdate();
  useEffect(() => {
    if (!currentColumns?.length || !availableColumns?.length) return;

    const columnConfiguration = availableColumns.map(({ index, source }) => ({
      source,
      enabled: currentColumns.includes(index),
    }));

    if (isEqual(columnConfiguration, defaultConfiguration?.columns)) return;

    updateOne(
      'transaction-table-setup',
      {
        id: `${entityType}/columns`,
        data: {
          columns: columnConfiguration,
        },
      },
    );
  }, [availableColumns, currentColumns, entityType, updateOne, defaultConfiguration?.columns]);

  const [demoData, setDemoData] = useState<DemoData[]>([
    { ...dummyData, isLoading: false },
  ]);
  const notify = useNotify();

  useEffect(() => {
    if (isLoadingDefaultConfig || !defaultConfiguration?.rows) return;

    const mappedData = defaultConfiguration.rows.map((row) => ({
      isLoading: false,
      data: {
        ...dummyData.data,
        id: row.id,
      },
    }));
    setDemoData([{ ...dummyData, isLoading: false }, ...mappedData]);
  }, [defaultConfiguration?.rows, isLoadingDefaultConfig]);

  const onChangeSelectedRow = useCallback((newSelectedRow: string | undefined) => {
    if (isLoadingDefaultConfig || !defaultConfiguration?.rows) return;

    const rowToShowInGraph = defaultConfiguration.rows.find(({
      transactionTypeConfiguration,
    }) => JSON.stringify(transactionTypeConfiguration) === newSelectedRow)?.id ?? 'all';

    updateOne(
      'transaction-table-setup',
      {
        id: `${entityType}/rowToShow`,
        data: {
          rowToShowInGraph,
        },
      },
    );
  }, [
    defaultConfiguration,
    entityType,
    isLoadingDefaultConfig,
    updateOne,
  ]);

  const [create] = useCreate();
  const addRow = useCallback((
    transactionTypeConfiguration: TransactionTypeConfiguration,
  ) => {
    if (!hasRequiredPermissions) return;
    create(
      `transaction-table-setup/${entityType}/row`,
      {
        data: {
          transactionTypeConfiguration,
        },
      },
      {
        onSuccess: (data: TransactionTableSetupType) => {
          const mappedData = data.rows.map((row) => ({
            isLoading: false,
            data: {
              ...dummyData.data,
              id: row.id,
            },
          }));
          setDemoData([{ ...dummyData, isLoading: false }, ...mappedData]);
        },
        onError: () => {
          notify('Could not add row.', { type: 'error' });
        },

      },
    );
  }, [create, entityType, hasRequiredPermissions, notify]);

  const [deleteOne] = useDelete();
  const deleteRow = useCallback((
    rowId: string,
  ) => {
    if (!hasRequiredPermissions) return;
    deleteOne(
      `transaction-table-setup/${entityType}/row`,
      {
        id: rowId,
      },
      {
        onSuccess: (data: TransactionTableSetupType) => {
          const mappedData = data.rows.map((row) => ({
            isLoading: false,
            data: {
              ...dummyData.data,
              id: row.id,
            },
          }));
          setDemoData([{ ...dummyData, isLoading: false }, ...mappedData]);
        },
        onError: () => {
          notify('Could not delete row.', { type: 'error' });
        },
      },
    );
  }, [entityType, hasRequiredPermissions, notify, deleteOne]);

  if (!hasAccess(permissions, requiredPermission) || isLoadingDefaultConfig) return null;

  return (
    <>
      <Accordion>
        <AccordionSummary
          expandIcon={<ChevronRight />}
        >
          <Typography>{entityType}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RecordContextProvider value={{ id: { entityType } }}>
            <TransactionSummaryTable
              params={{
                from: '',
                to: '',
                currency: 'SEK',
                rollingSumWindow: null,
                distributionWindow: null,
                maxDepth: undefined,
                selectedRow: undefined,
                flowSort: undefined,
                flowRelatedEntityType: undefined,
                flowRelatedRoles: undefined,
                flowPageSize: 0,
                entityId: '',
                entityType,
              }}
              filter={{
                from: '',
                to: '',
                currency: '',
                entityId: '',
                entityType,
                requestType: 'transaction',
              }}
              preferenceKey={preferenceKey}
              defaultConfiguration={defaultConfiguration}
              onChangeSelectedRow={onChangeSelectedRow}
              demo={{
                demoData,
                addRow,
                deleteRow,
              }}
            />
          </RecordContextProvider>
        </AccordionDetails>
      </Accordion>
      <Divider />
    </>
  );
};

const TransactionTableSetup = () => {
  const { permissions } = usePermissions<Actions[]>();

  return (
    <Paper>
      <TabbedShowLayout record={{ id: '' }}>
        <TabbedShowLayout.Tab label="Transaction table setup">
          {hasAccess(permissions, actions.TRANSACTION_TABLE_SETUP_GET) && (
            <Box display="flex" flexDirection="column" width="stretch">
              {Object.values(entityTypes)
                .filter((type): type is KnownEntityTypes => (
                  type !== entityTypes.UNKNOWN
                ))
                .map((entityType) => (
                  <Form key={entityType} record={{}}>
                    <EntityTransactionTableSetup entityType={entityType} />
                  </Form>
                ))}
            </Box>
          )}
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Paper>
  );
};

export default TransactionTableSetup;
